import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp, TrendingDown, Wallet, PiggyBank, Target,
  ArrowRight, RefreshCw, Sparkles, PieChart, AlertTriangle,
  CheckCircle, Info, ChevronRight, Building2, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { motion } from 'framer-motion';
import {
  PieChart as RechartPie, Pie, Cell, ResponsiveContainer, Tooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Area, AreaChart
} from 'recharts';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Helpers ──
const fmtCur = (v) => {
  const abs = Math.abs(v || 0);
  const f = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(abs);
  return v < 0 ? `-${f}` : f;
};
const fmtShort = (v) => {
  const abs = Math.abs(v || 0);
  if (abs >= 1e6) return `${(v / 1e6).toFixed(1)}M `;
  if (abs >= 1e3) return `${(v / 1e3).toFixed(0)}k `;
  return `${v} `;
};
const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
const ALLOC_COLORS = { ETF: '#00e5a0', Actions: '#4f8eff', Crypto: '#7c3aed', Immobilier: '#f97316', Épargne: '#22d3ee', Autres: '#94a3b8' };

// ── KPI Card ──
const KpiCard = ({ title, value, subtitle, icon: Icon, color, idx, testId }) => {
  const bg = { emerald: 'bg-[#00e5a0]/10', blue: 'bg-[#4f8eff]/10', orange: 'bg-[#f97316]/10', red: 'bg-red-500/10', purple: 'bg-[#7c3aed]/10' };
  const text = { emerald: 'text-[#00e5a0]', blue: 'text-[#4f8eff]', orange: 'text-[#f97316]', red: 'text-red-400', purple: 'text-[#7c3aed]' };
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
      className="bg-[#141720] border border-white/5 rounded-2xl p-5 hover:border-white/10 transition-all"
      data-testid={testId}>
      <div className={`p-2.5 rounded-xl w-fit mb-3 ${bg[color] || bg.emerald}`}>
        <Icon className={`w-5 h-5 ${text[color] || text.emerald}`} />
      </div>
      <p className="text-cap-text-secondary text-sm mb-1">{title}</p>
      <p className={`font-display text-2xl lg:text-3xl font-bold ${text[color] || text.emerald}`}>{value}</p>
      <p className="text-cap-text-muted text-xs mt-1">{subtitle}</p>
    </motion.div>
  );
};

// ── Main ──
const DashboardPage = () => {
  const { token, user } = useAuth();
  const userId = user?.id || 'anon';
  const [debts, setDebts] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [capScore, setCapScore] = useState(0);
  const [nextAction, setNextAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Patrimoine blob (positions, savings, realestate, otherassets) — fetched from db.user_data
  const [positions, setPositions] = useState([]);
  const [savingsAccounts, setSavingsAccounts] = useState([]);
  const [realEstate, setRealEstate] = useState([]);
  const [otherAssets, setOtherAssets] = useState([]);
  // Finances blob (budget, monthly map) — also from db.user_data
  const [financesBlob, setFinancesBlob] = useState({});

  // localStorage helper (still used for price cache & finances)
  const ls = useMemo(() => ({
    get: (k, fb) => { try { const v = localStorage.getItem(`capos_${userId}_${k}`); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  }), [userId]);

  // ── Fetch backend data ──
  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/debts`, { headers: h }).then(r => setDebts(r.data)).catch(() => {}),
      axios.get(`${API}/objectives`, { headers: h }).then(r => setObjectives(r.data)).catch(() => {}),
      axios.get(`${API}/dashboard/kpis`, { headers: h }).then(r => {
        setCapScore(r.data.cap_score || 0);
        setNextAction(r.data.next_action || null);
      }).catch(() => {}),
      // Patrimoine blob lives in db.user_data (migrated from localStorage). Aligned with PatrimoinePage.
      axios.get(`${API}/store/patrimoine`, { headers: h }).then(r => {
        const d = r.data || {};
        setPositions(d.positions || []);
        setSavingsAccounts(d.savings || []);
        setRealEstate(d.realestate || []);
        setOtherAssets(d.otherassets || []);
      }).catch(() => {}),
      // Finances blob (budget, monthly map) — aligned with FinancesPage storeRef.
      axios.get(`${API}/store/finances`, { headers: h }).then(r => {
        setFinancesBlob(r.data || {});
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [token, refreshKey]);

  // Price cache stays in localStorage (refreshed by PatrimoinePage); used as fallback only.
  const priceCache = ls.get('ptr_prices', {});

  const patrimoine = useMemo(() => {
    let invest = 0, savings = 0, re = 0, oa = 0, debtTotal = 0;
    const byType = { ETF: 0, Actions: 0, Crypto: 0 };
    positions.forEach(p => {
      const price = priceCache[p.ticker]?.price || p.price || 0;
      const val = (p.quantity || 0) * price;
      invest += val;
      const t = p.type || 'ETF';
      if (t in byType) byType[t] += val; else byType.ETF += val;
    });
    savingsAccounts.forEach(s => { savings += parseFloat(s.balance) || 0; });
    realEstate.forEach(r => { re += parseFloat(r.value) || 0; });
    otherAssets.forEach(a => { oa += parseFloat(a.value) || 0; });
    debts.forEach(d => { debtTotal += parseFloat(d.remaining_amount || d.total_amount) || 0; });

    const brut = invest + savings + re + oa;
    const net = brut - debtTotal;
    return { invest, savings, re, oa, brut, debtTotal, net, byType, reEstimated: false };
  }, [positions, savingsAccounts, realEstate, otherAssets, priceCache, debts]);

  // ── Finances from db.user_data (migrated from localStorage) ──
  const currentMonthKey = useMemo(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const monthData = (financesBlob.monthly || {})[currentMonthKey] || { revenues: [], expenses: [] };
  const budget = financesBlob.budget || { revenues: [], fixedCharges: [], variableCharges: [], envelopes: [] };

  const finances = useMemo(() => {
    const totalRevenues = (monthData.revenues || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
    const totalExpenses = (monthData.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const cashflow = totalRevenues - totalExpenses;
    // Taux d'épargne = (enveloppes épargne ou cashflow positif) / revenus
    const envTotal = (budget.envelopes || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
    const savingsRate = totalRevenues > 0 ? ((envTotal > 0 ? envTotal : Math.max(cashflow, 0)) / totalRevenues) * 100 : null;
    return { totalRevenues, totalExpenses, cashflow, savingsRate };
  }, [monthData, budget]);

  // ── Allocation data for donut ──
  const allocationData = useMemo(() => {
    const d = [];
    if (patrimoine.byType.ETF > 0) d.push({ name: 'ETF', value: patrimoine.byType.ETF });
    if (patrimoine.byType.Actions > 0) d.push({ name: 'Actions', value: patrimoine.byType.Actions });
    if (patrimoine.byType.Crypto > 0) d.push({ name: 'Crypto', value: patrimoine.byType.Crypto });
    if (patrimoine.re > 0) d.push({ name: 'Immobilier', value: patrimoine.re });
    if (patrimoine.savings > 0) d.push({ name: 'Épargne', value: patrimoine.savings });
    if (patrimoine.oa > 0) d.push({ name: 'Autres', value: patrimoine.oa });
    return d;
  }, [patrimoine]);

  // ── 12-month net worth evolution ──
  const evolutionData = useMemo(() => {
    const n = new Date();
    const data = [];
    const monthly = financesBlob.monthly || {};
    for (let i = 11; i >= 0; i--) {
      const d = new Date(n.getFullYear(), n.getMonth() - i, 1);
      const mKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const mData = monthly[mKey] || null;
      const label = MONTH_LABELS[d.getMonth()];
      if (mData) {
        const rev = (mData.revenues || []).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const exp = (mData.expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
        data.push({ month: label, value: rev - exp, hasData: true });
      } else {
        data.push({ month: label, value: null, hasData: false });
      }
    }
    // Build cumulative from current net worth backwards
    let cumul = patrimoine.net;
    const result = [];
    for (let i = data.length - 1; i >= 0; i--) {
      result.unshift({ month: data[i].month, net: Math.round(cumul), hasData: data[i].hasData });
      if (data[i].value !== null) cumul -= data[i].value;
    }
    return result;
  }, [patrimoine.net, financesBlob]);

  // ── Last 5 transactions ──
  const lastTransactions = useMemo(() => {
    const all = [];
    (monthData.revenues || []).forEach(r => all.push({ ...r, txType: 'revenue' }));
    (monthData.expenses || []).forEach(e => all.push({ ...e, txType: 'expense' }));
    all.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return all.slice(0, 5);
  }, [monthData]);

  // ── Insights ──
  const insights = useMemo(() => {
    const arr = [];
    if (finances.savingsRate !== null && finances.savingsRate < 10)
      arr.push({ type: 'warning', icon: AlertTriangle, text: `Ton taux d'épargne est de ${finances.savingsRate.toFixed(0)}% ce mois. Objectif recommandé : 20% minimum.` });
    if (finances.cashflow < 0)
      arr.push({ type: 'warning', icon: TrendingDown, text: `Ton cashflow est négatif ce mois. Tes dépenses dépassent tes revenus de ${fmtCur(Math.abs(finances.cashflow))}.` });
    objectives.forEach(o => {
      const pct = o.target_amount > 0 ? (o.current_amount / o.target_amount) * 100 : 0;
      if (pct >= 80 && pct < 100) {
        const remaining = o.target_amount - o.current_amount;
        arr.push({ type: 'success', icon: Target, text: `Tu es à ${pct.toFixed(0)}% de ton objectif "${o.name}" ! Plus que ${fmtCur(remaining)} à atteindre.` });
      }
    });
    // Check evolution
    if (evolutionData.length >= 2) {
      const cur = evolutionData[evolutionData.length - 1]?.net || 0;
      const prev = evolutionData[evolutionData.length - 2]?.net || 0;
      const diff = cur - prev;
      if (diff > 0) arr.push({ type: 'info', icon: TrendingUp, text: `Ton patrimoine a progressé de ${fmtCur(diff)} ce mois.` });
    }
    if (finances.savingsRate !== null && finances.savingsRate >= 20)
      arr.push({ type: 'success', icon: CheckCircle, text: `Excellent ! Ton taux d'épargne est de ${finances.savingsRate.toFixed(0)}%, au-dessus de l'objectif de 20%.` });
    return arr.slice(0, 3);
  }, [finances, objectives, evolutionData]);

  const savingsColor = finances.savingsRate === null ? 'blue' : finances.savingsRate >= 20 ? 'emerald' : finances.savingsRate >= 10 ? 'orange' : 'red';
  const cashflowColor = finances.cashflow >= 0 ? 'emerald' : 'red';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <RefreshCw className="w-8 h-8 text-[#00e5a0] animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white mb-1">
            Bonjour, {user?.username}
          </h1>
          <p className="text-cap-text-secondary text-sm">Voici ta situation financière en un coup d'oeil</p>
        </div>
        <Button onClick={() => setRefreshKey(k => k + 1)} variant="ghost" className="text-cap-text-secondary hover:text-white"
          data-testid="refresh-dashboard-btn">
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      {/* LIGNE 1 — 4 KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Patrimoine net" value={fmtCur(patrimoine.net)} idx={0} testId="kpi-patrimoine"
          subtitle={`Actifs : ${fmtCur(patrimoine.brut)} | Dettes : ${fmtCur(patrimoine.debtTotal)}${patrimoine.reEstimated ? ' (valorisation estimée)' : ''}`}
          icon={Wallet} color={patrimoine.net >= 0 ? 'emerald' : 'red'} />
        <KpiCard title="Cashflow ce mois" value={finances.totalRevenues > 0 ? fmtCur(finances.cashflow) : '—'} idx={1} testId="kpi-cashflow"
          subtitle={finances.totalRevenues > 0 ? `Revenus ${fmtCur(finances.totalRevenues)} - Dépenses ${fmtCur(finances.totalExpenses)}` : 'Non configuré'}
          icon={finances.cashflow >= 0 ? TrendingUp : TrendingDown} color={finances.totalRevenues > 0 ? cashflowColor : 'blue'} />
        <KpiCard title="Taux d'épargne" value={finances.savingsRate !== null ? `${finances.savingsRate.toFixed(1)}%` : '—'} idx={2} testId="kpi-savings-rate"
          subtitle={finances.savingsRate !== null ? 'Objectif recommandé : 20%' : 'Non configuré'}
          icon={PiggyBank} color={savingsColor} />
        <KpiCard title="CAP Score" value={capScore} idx={3} testId="kpi-cap-score"
          subtitle="Sur 100 points" icon={Target} color="purple" />
      </div>

      {/* LIGNE 2 — Donut + Line chart */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Donut */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          className="bg-[#141720] border border-white/5 rounded-2xl p-6 lg:col-span-5" data-testid="allocation-donut">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-5 h-5 text-cap-text-secondary" />
            <h3 className="font-display text-base font-semibold text-white">Répartition patrimoine</h3>
          </div>
          {allocationData.length > 0 ? (
            <div className="flex items-center gap-4">
              <div className="w-40 h-40 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <RechartPie>
                    <Pie data={allocationData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={2} dataKey="value">
                      {allocationData.map(e => <Cell key={e.name} fill={ALLOC_COLORS[e.name] || '#94a3b8'} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                      formatter={(v) => fmtCur(v)} />
                  </RechartPie>
                </ResponsiveContainer>
              </div>
              <div className="space-y-2.5 flex-1">
                {allocationData.map(item => (
                  <div key={item.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: ALLOC_COLORS[item.name] || '#94a3b8' }} />
                      <span className="text-cap-text-secondary text-xs">{item.name}</span>
                    </div>
                    <span className="text-white font-mono text-xs font-medium">{fmtCur(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="h-40 flex flex-col items-center justify-center">
              <p className="text-cap-text-muted text-sm mb-3">Aucun actif renseigné</p>
              <Link to="/patrimoine"><Button variant="ghost" className="text-[#00e5a0] text-xs">Configurer mon patrimoine <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
          )}
        </motion.div>

        {/* Line chart */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="bg-[#141720] border border-white/5 rounded-2xl p-6 lg:col-span-7" data-testid="evolution-chart">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-cap-text-secondary" />
            <h3 className="font-display text-base font-semibold text-white">Évolution patrimoine net</h3>
          </div>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={evolutionData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="greenGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00e5a0" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#00e5a0" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={fmtShort} width={50} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1d27', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(v) => [fmtCur(v), 'Patrimoine net']} />
                <Area type="monotone" dataKey="net" stroke="#00e5a0" strokeWidth={2} fill="url(#greenGrad)" dot={{ fill: '#00e5a0', r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* LIGNE 3 — Objectifs + Transactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Objectifs */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-[#141720] border border-white/5 rounded-2xl p-6" data-testid="objectives-section">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-cap-text-secondary" />
              <h3 className="font-display text-base font-semibold text-white">Objectifs en cours</h3>
            </div>
            <Link to="/objectives">
              <Button variant="ghost" className="text-cap-text-secondary hover:text-white text-xs h-7 px-2">
                Voir tout <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          {objectives.length > 0 ? (
            <div className="space-y-3">
              {objectives.slice(0, 3).map(o => {
                const pct = o.target_amount > 0 ? Math.min((o.current_amount / o.target_amount) * 100, 100) : 0;
                const remaining = Math.max(o.target_amount - o.current_amount, 0);
                return (
                  <div key={o.id} className="p-3.5 bg-[#0d0f14] rounded-xl" data-testid={`objective-${o.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium text-sm">{o.name}</span>
                      <span className="text-cap-text-muted text-xs">{fmtCur(remaining)} restants</span>
                    </div>
                    <Progress value={pct} className="h-2.5 bg-white/5" />
                    <div className="flex items-center justify-between mt-1.5">
                      <span className="text-cap-text-muted text-[10px]">{fmtCur(o.current_amount)} / {fmtCur(o.target_amount)}</span>
                      <span className="text-[#00e5a0] text-xs font-bold">{pct.toFixed(0)}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-36 flex flex-col items-center justify-center">
              <Target className="w-10 h-10 text-cap-text-muted mb-3" />
              <p className="text-cap-text-muted text-sm mb-3">Aucun objectif défini</p>
              <Link to="/objectives"><Button className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-4">Créer un objectif</Button></Link>
            </div>
          )}
        </motion.div>

        {/* Dernières transactions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="bg-[#141720] border border-white/5 rounded-2xl p-6" data-testid="last-transactions">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-cap-text-secondary" />
              <h3 className="font-display text-base font-semibold text-white">Dernières transactions</h3>
            </div>
            <Link to="/finances">
              <Button variant="ghost" className="text-cap-text-secondary hover:text-white text-xs h-7 px-2">
                Voir tout <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </Link>
          </div>
          {lastTransactions.length > 0 ? (
            <div className="space-y-2">
              {lastTransactions.map((tx, i) => {
                const isRev = tx.txType === 'revenue';
                return (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#0d0f14] rounded-xl" data-testid={`tx-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isRev ? 'bg-[#00e5a0]/10' : 'bg-red-500/10'}`}>
                        {isRev ? <TrendingUp className="w-4 h-4 text-[#00e5a0]" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
                      </div>
                      <div>
                        <p className="text-white text-sm">{tx.description || tx.name || tx.category || (isRev ? 'Revenu' : 'Dépense')}</p>
                        <p className="text-cap-text-muted text-[10px]">{tx.category || tx.subcategory || ''}{tx.date ? ` · ${new Date(tx.date).toLocaleDateString('fr-FR')}` : ''}</p>
                      </div>
                    </div>
                    <span className={`font-mono text-sm font-bold ${isRev ? 'text-[#00e5a0]' : 'text-red-400'}`}>
                      {isRev ? '+' : '-'}{fmtCur(parseFloat(tx.amount) || 0)}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-36 flex flex-col items-center justify-center">
              <Banknote className="w-10 h-10 text-cap-text-muted mb-3" />
              <p className="text-cap-text-muted text-sm mb-3">Aucune transaction ce mois</p>
              <Link to="/finances"><Button variant="ghost" className="text-[#00e5a0] text-xs">Configurer mes finances <ArrowRight className="w-3 h-3 ml-1" /></Button></Link>
            </div>
          )}
        </motion.div>
      </div>

      {/* LIGNE 4 — Insights */}
      {insights.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          className="flex gap-4 overflow-x-auto pb-2" data-testid="insights-section">
          {insights.map((ins, i) => {
            const colors = { warning: 'border-amber-500/30 bg-amber-500/5', success: 'border-[#00e5a0]/30 bg-[#00e5a0]/5', info: 'border-[#4f8eff]/30 bg-[#4f8eff]/5' };
            const iconColors = { warning: 'text-amber-400', success: 'text-[#00e5a0]', info: 'text-[#4f8eff]' };
            return (
              <div key={i} className={`flex items-center gap-3 border rounded-xl p-4 min-w-[300px] flex-shrink-0 ${colors[ins.type]}`} data-testid={`insight-${i}`}>
                <ins.icon className={`w-5 h-5 flex-shrink-0 ${iconColors[ins.type]}`} />
                <p className="text-white text-sm">{ins.text}</p>
              </div>
            );
          })}
        </motion.div>
      )}

      {/* Next action */}
      {nextAction && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="bg-[#141720] border border-white/5 border-l-4 border-l-[#00e5a0] rounded-2xl p-5 flex items-center gap-4" data-testid="next-move-card">
          <div className="p-2.5 rounded-xl bg-[#00e5a0]/10">
            <Sparkles className="w-5 h-5 text-[#00e5a0]" />
          </div>
          <div className="flex-1">
            <p className="text-cap-text-secondary text-xs mb-0.5">Prochaine action recommandée</p>
            <p className="font-display text-sm font-semibold text-white">{nextAction}</p>
          </div>
          <Link to="/profile">
            <Button className="bg-[#00e5a0] text-[#0d0f14] font-semibold text-xs h-8 px-4 hover:bg-[#00e5a0]/80">
              CAP Score <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </Link>
        </motion.div>
      )}
    </div>
  );
};

export default DashboardPage;
